import React, { useState } from "react";
import Stepper from "../../../components/Stepper/Stepper";
import "../BookingStepper/bookingStepper.css";
import "./publicBooking.css";

const money = (currency, n) => `${currency} ${n}`;

// The booking at a glance. `rows` are the same price rows the old page showed in its
// breakdown table, so every number matches what the customer will be charged.
export function PublicSummary({ summary, steps }) {
  const { currency, rows = [], services = [], when, doctor, doctorLabel, customer, customerLabel, serviceLabel } = summary;
  const total = rows.find((r) => String(r?.name || "").toLowerCase().includes("total"));
  return (
    <div className="bstep-summary" aria-label="Booking summary">
      <div className="bstep-summary-body is-open" style={{ display: "block" }}>
        <div className="bstep-summary-title">Summary</div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">{serviceLabel}</div>
          {services.length === 0 ? (
            <div className="bstep-muted">Nothing chosen yet</div>
          ) : (
            services.map((s) => (
              <div key={s.key} className="bstep-sum-line">
                <span>{s.name}</span>
                {s.amount !== undefined && s.amount !== null && <span>{money(currency, s.amount)}</span>}
              </div>
            ))
          )}
        </div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">When</div>
          {when ? <div className="bstep-sum-value">{when}</div> : <div className="bstep-muted">Not chosen yet</div>}
          {doctor && (
            <>
              <div className="bstep-sum-label mt-2">{doctorLabel}</div>
              <div className="bstep-sum-value">{doctor}</div>
            </>
          )}
        </div>

        <div className="bstep-sum-block">
          <div className="bstep-sum-label">{customerLabel}</div>
          {customer?.name || customer?.phone ? (
            <>
              <div className="bstep-sum-value">{customer.name || "—"}</div>
              {customer.phone && <div className="bstep-muted">+{customer.phone}</div>}
            </>
          ) : (
            <div className="bstep-muted">Not entered yet</div>
          )}
        </div>

        <div className="bstep-sum-block bstep-sum-money">
          {rows.length > 0 ? (
            rows.map((r) => (
              <div key={r.id || r.name} className={`bstep-sum-line ${total === r ? "is-total" : ""}`}>
                <span>{r.name}</span>
                <span>{money(currency, r.amount)}</span>
              </div>
            ))
          ) : (
            <div className="bstep-muted">The total appears once a service is chosen.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Presentation of the public booking link as a guided flow. Owns no booking logic:
 * CustomerBooking.jsx keeps every field, rule, API call and payment path and hands this
 * component the already-built step content and handlers.
 */
export default function PublicBookingShell({
  title,
  photo,
  image,
  steps,
  stepIndex,
  completed,
  invalid,
  onSelectStep,
  panes,
  summary,
  onBack,
  onNext,
  isLast,
  busy,
  submitLabel,
  actionsHidden,
  verifying,
  contact,
  success,
  total,
  currency,
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const stepKey = steps[stepIndex]?.key;

  const pane = (key, node) => (
    <div className="bstep-pane" hidden={stepKey !== key} data-step={key}>
      {node}
    </div>
  );

  const primary = (
    <button type="button" className="btn btn-danger px-4" onClick={isLast ? undefined : onNext} data-action={isLast ? "submit" : "next"} disabled={busy} {...(isLast ? { type: "submit" } : {})}>
      {busy ? "Please wait…" : isLast ? submitLabel : "Next"}
    </button>
  );

  return (
    <div className="booking-bg">
      <div className="container py-3 bstep pbk">
        {verifying && <div className="bstep-progress" role="progressbar" aria-label="Verifying payment" />}

        <header className="pbk-header">
          <h2>{title}</h2>
          {photo && <img className="pbk-avatar" src={photo} alt="" />}
        </header>

        {success ? (
          success
        ) : (
          <>
            <div className="bstep-stepper">
              <Stepper steps={steps} current={stepIndex} completed={completed} invalid={invalid} onSelect={onSelectStep} />
            </div>

            <div className="pbk-layout">
              <aside className="pbk-aside">
                {image && <img className="pbk-image" src={image} alt="" />}
                <div className="pbk-summary-desktop">
                  <PublicSummary summary={summary} steps={steps} />
                </div>
              </aside>

              <div className="pbk-main">
                {pane("service", panes.service)}
                {pane("schedule", panes.schedule)}
                {pane("details", panes.details)}
                {pane("review", panes.review)}

                {!actionsHidden && (
                  <div className="bstep-footer pbk-footer">
                    <div />
                    <div className="bstep-footer-right">
                      {stepIndex > 0 && (
                        <button type="button" className="btn btn-outline-secondary px-4" onClick={onBack} disabled={busy}>
                          Back
                        </button>
                      )}
                      {primary}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {contact}
      </div>

      {/* phone / tablet: running total + the main action, always in reach */}
      {!success && (
        <>
          <div className="pbk-bar">
            <button type="button" className="pbk-bar-total" onClick={() => setSheetOpen(true)} aria-haspopup="dialog" aria-label="View booking summary">
              <small>Total · tap for summary</small>
              <strong>{total !== null && total !== undefined ? money(currency, total) : "—"}</strong>
            </button>
            {!actionsHidden && (
              <>
                {stepIndex > 0 && (
                  <button type="button" className="btn btn-outline-secondary pbk-bar-back" onClick={onBack} disabled={busy}>
                    Back
                  </button>
                )}
                {primary}
              </>
            )}
          </div>

          {sheetOpen && (
            <>
              <div className="pbk-sheet-scrim" onClick={() => setSheetOpen(false)} />
              <div className="pbk-sheet" role="dialog" aria-modal="true" aria-label="Booking summary">
                <div className="pbk-sheet-head">
                  <span>Booking summary</span>
                  <button type="button" className="pbk-sheet-close" onClick={() => setSheetOpen(false)} aria-label="Close summary">
                    ✕
                  </button>
                </div>
                <PublicSummary summary={summary} steps={steps} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
